<script lang="ts">
  import Icon from '../icons/Icon.svelte';
  const items = [
    {
      q: 'What happens if I lose my device?',
      a: "You're responsible for backups — and we make backups easy. VuAuth supports offline encrypted exports, printable paper backups with rotational checksums, and Beam-to-laptop QR handoffs. We don't keep a copy because there's no server to keep one on. That's the same reason no one else can take a copy without your consent."
    },
    {
      q: 'How is this different from Aegis?',
      a: "Aegis is excellent and shares our local-only architecture. VuAuth differs in three places: (1) it's a PWA so it runs anywhere a browser does, including desktop, (2) the V3 UX innovations — Site Detective, Time Crystal, Code Teleport, Stage Mode — were designed to attack specific friction moments Aegis doesn't address, (3) Beam offers QR-based device-to-device handoff without the manual export/import flow."
    },
    {
      q: 'Why is VuAuth free?',
      a: "Because security should be accessible to everyone. VuAuth is free, with no data harvesting, no ads, no upsell. Other \"free\" authenticators are funded by something — usually data harvesting (Authy, Microsoft Authenticator) or a corporate sync sale (Google). With VuAuth, you are never the product."
    },
    {
      q: 'Is the cryptography really verified?',
      a: "Yes — VuAuth implements RFC 6238 TOTP via WebCrypto's HMAC primitives, and all five published Appendix B test vectors pass. The vault uses AES-GCM-256 with PBKDF2-SHA256 at 210,000 iterations, sized to OWASP 2023 guidance. The full source is available; the math is visible at runtime via the Setup Verifier+ feature."
    },
    {
      q: 'What about cloud sync? I have ten devices.',
      a: 'Beam handles device-to-device handoff via offline QR, no network involved. For continuous multi-device sync, the honest answer is that "no servers" and "automatic cross-device sync" are architecturally in tension. We chose no servers. If you need true continuous sync across many devices, Ente Auth (E2E with account) is a more honest fit than the closed-source vendors that promise both.'
    },
    {
      q: 'Can I import from another authenticator?',
      a: 'Yes. VuAuth supports standard otpauth:// URIs, QR scanning, and direct paste from Aegis JSON exports, 2FAS encrypted backups, Google Authenticator transfer QRs, and plain TOTP secrets. The Cmd-Pal Universal palette accepts pasted URIs and adds them in one keystroke.'
    }
  ];
</script>

<section id="faq" class="section scroll-reveal">
  <div class="wrap">
    <span class="eyebrow">Honest answers</span>
    <h2 class="h-section heading">Questions <em>worth asking.</em></h2>

    <div class="faq-wrap">
      {#each items as item, i (i)}
        <details class="faq-item">
          <summary class="faq-q">
            <span>{item.q}</span>
            <span class="faq-q-icon"><Icon name="plus" size={20} /></span>
          </summary>
          <div class="faq-a">{item.a}</div>
        </details>
      {/each}
    </div>
  </div>
</section>

<style>
  .section {
    padding: 96px 0;
    position: relative;
  }
  @media (max-width: 640px) {
    .section {
      padding: 64px 0;
    }
  }
  :global([data-theme='brutalist']) .section {
    border-bottom: 2px solid var(--text);
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }
  .eyebrow {
    display: inline-flex;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    padding: 6px 12px;
    border: 1px solid var(--border-2);
    border-radius: 999px;
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .eyebrow {
    border-radius: 0;
    border: 2px solid var(--text);
    background: var(--accent-dim);
    color: var(--text);
    font-weight: 800;
  }
  .heading {
    margin-top: 18px;
  }
  .h-section {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .h-section em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .h-section {
    font-weight: 900;
  }
  :global([data-theme='brutalist']) .h-section em {
    color: var(--text);
  }
  .faq-wrap {
    max-width: 820px;
    margin: 56px auto 0;
  }
  .faq-item {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    margin-bottom: 12px;
    overflow: hidden;
    background: var(--bg-card);
    transition:
      border-color var(--t),
      background var(--t);
  }
  :global([data-theme='brutalist']) .faq-item {
    border: 2px solid var(--text);
    box-shadow: 4px 4px 0 var(--text);
  }
  .faq-item[open] {
    border-color: var(--border-2);
    background: var(--bg-card-hover);
  }
  .faq-q {
    list-style: none;
    padding: 18px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    cursor: pointer;
    user-select: none;
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
  }
  :global([data-theme='brutalist']) .faq-q {
    font-weight: 800;
  }
  .faq-q::-webkit-details-marker {
    display: none;
  }
  .faq-q-icon {
    flex-shrink: 0;
    transition: transform var(--t);
    display: inline-flex;
  }
  .faq-item[open] .faq-q-icon {
    transform: rotate(45deg);
  }
  .faq-a {
    padding: 0 22px 18px;
    font-size: 14px;
    color: var(--text-2);
    line-height: 1.65;
  }
</style>
